import {
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonModal,
    IonSpinner,
    IonTitle,
    IonToolbar
} from "@ionic/react";
import {
    useFantasyModalBackGuard
} from "./useFantasyModalBackGuard";

import {
    checkmarkCircleOutline,
    lockClosedOutline,
    swapHorizontalOutline
} from "ionicons/icons";

import {
    useEffect,
    useMemo,
    useState
} from "react";

import "./FantasyTradeOfferModal.css";

export interface FantasyTradeCandidate {
    id: string;
    name: string;
    position: string;
    realTeamName: string;
    isLocked?: boolean;
}

interface FantasyTradeOfferModalProps {
    isOpen: boolean;

    requestedPlayer: {
        id: string;
        name: string;
        position: string;
        realTeamName: string;
        ownerName: string;
    } | null;

    candidates: FantasyTradeCandidate[];

    isSubmitting?: boolean;

    onDismiss: () => void;

    onSubmit: (
        offeredPlayerId: string
    ) => void;
}

function getPositionClass(
    position: string
): string {
    return String(position || "")
        .trim()
        .toLowerCase();
}

const FantasyTradeOfferModal: React.FC<
    FantasyTradeOfferModalProps
> = function ({
    isOpen,
    requestedPlayer,
    candidates,
    isSubmitting = false,
    onDismiss,
    onSubmit
}) {
    const [
        offeredPlayerId,
        setOfferedPlayerId
    ] = useState("");

    useEffect(
        function () {
            if (!isOpen) {
                setOfferedPlayerId("");
            }
        },
        [isOpen]
    );

    const availableCandidates = useMemo(
        function (): FantasyTradeCandidate[] {
            return candidates.filter(
                function (player) {
                    return !player.isLocked;
                }
            );
        },
        [candidates]
    );

    const selectedCandidate = useMemo(
        function (): FantasyTradeCandidate | null {
            return (
                availableCandidates.find(
                    function (player) {
                        return player.id === offeredPlayerId;
                    }
                ) || null
            );
        },
        [
            availableCandidates,
            offeredPlayerId
        ]
    );

useFantasyModalBackGuard({
    isOpen,
    onDismiss,
    priority: 30000,
    canDismiss: !isSubmitting
});

    return (
<IonModal
    isOpen={isOpen}
    onDidDismiss={onDismiss}
    backdropDismiss={!isSubmitting}
>


            <IonHeader>
                <IonToolbar className="fantasy-trade-toolbar">
                    <IonTitle>
                        Proponer intercambio
                    </IonTitle>

                    <IonButtons slot="end">
                        <IonButton
                            disabled={isSubmitting}
                            onClick={onDismiss}
                        >
                            Cerrar
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent className="fantasy-trade-content">
                {!requestedPlayer ? (
                    <div className="fantasy-trade-empty">
                        No fue posible identificar al jugador solicitado.
                    </div>
                ) : (
                    <main className="fantasy-trade-main">
                        <section className="fantasy-trade-hero">
                            <span>INTERCAMBIO</span>

                            <h1>
                                Elige qué jugador ofrecerás
                            </h1>

                            <p>
                                Selecciona un jugador desbloqueado de tu roster.
                            </p>
                        </section>

                        <section className="fantasy-trade-section">
                            <span className="fantasy-trade-section__label">
                                QUIERES RECIBIR
                            </span>

                            <article className="fantasy-trade-player fantasy-trade-player--requested">
                                <span
                                    className={[
                                        "fantasy-trade-player__position",
                                        `fantasy-trade-player__position--${getPositionClass(
                                            requestedPlayer.position
                                        )}`
                                    ].join(" ")}
                                >
                                    {requestedPlayer.position}
                                </span>

                                <div className="fantasy-trade-player__details">
                                    <strong>
                                        {requestedPlayer.name}
                                    </strong>

                                    <small>
                                        {requestedPlayer.realTeamName}
                                    </small>

                                    {requestedPlayer.ownerName && (
                                        <em>
                                            Roster de{" "}
                                            {requestedPlayer.ownerName}
                                        </em>
                                    )}
                                </div>

                                <IonIcon
                                    icon={swapHorizontalOutline}
                                    className="fantasy-trade-player__exchange-icon"
                                />
                            </article>
                        </section>

                        <section className="fantasy-trade-section">
                            <div className="fantasy-trade-section__heading">
                                <div>
                                    <span className="fantasy-trade-section__label">
                                        OFRECES
                                    </span>

                                    <h2>
                                        Tu roster
                                    </h2>
                                </div>

                                <small>
                                    {availableCandidates.length} disponibles
                                </small>
                            </div>

                            {candidates.length === 0 ? (
                                <div className="fantasy-trade-empty">
                                    Tu roster no tiene jugadores disponibles para ofrecer.
                                </div>
                            ) : (
                                <div className="fantasy-trade-list">
                                    {candidates.map(
                                        function (player) {
                                            const isLocked = Boolean(
                                                player.isLocked
                                            );

                                            const isSelected =
                                                player.id ===
                                                offeredPlayerId;

                                            return (
                                                <button
                                                    key={player.id}
                                                    type="button"
                                                    disabled={
                                                        isSubmitting ||
                                                        isLocked
                                                    }
                                                    className={[
                                                        "fantasy-trade-candidate",
                                                        isSelected
                                                            ? "fantasy-trade-candidate--selected"
                                                            : "",
                                                        isLocked
                                                            ? "fantasy-trade-candidate--locked"
                                                            : ""
                                                    ].join(" ")}
                                                    onClick={function () {
                                                        if (!isLocked) {
                                                            setOfferedPlayerId(
                                                                player.id
                                                            );
                                                        }
                                                    }}
                                                >
                                                    <span
                                                        className={[
                                                            "fantasy-trade-candidate__position",
                                                            `fantasy-trade-candidate__position--${getPositionClass(
                                                                player.position
                                                            )}`
                                                        ].join(" ")}
                                                    >
                                                        {player.position}
                                                    </span>

                                                    <span className="fantasy-trade-candidate__details">
                                                        <strong>
                                                            {player.name}
                                                        </strong>

                                                        <small>
                                                            {player.realTeamName}
                                                        </small>

                                                        {isLocked && (
                                                            <em>
                                                                <IonIcon
                                                                    icon={lockClosedOutline}
                                                                />

                                                                Partido iniciado
                                                            </em>
                                                        )}
                                                    </span>

                                                    {isSelected && (
                                                        <IonIcon
                                                            icon={checkmarkCircleOutline}
                                                            className="fantasy-trade-candidate__selected-icon"
                                                        />
                                                    )}
                                                </button>
                                            );
                                        }
                                    )}
                                </div>
                            )}
                        </section>

                        {selectedCandidate && (
                            <section className="fantasy-trade-confirmation">
                                <span>PROPUESTA</span>

                                <strong>
                                    {selectedCandidate.name}
                                    {" por "}
                                    {requestedPlayer.name}
                                </strong>
                            </section>
                        )}

                        <IonButton
                            expand="block"
                            className="fantasy-trade-submit"
                            disabled={
                                isSubmitting ||
                                offeredPlayerId === ""
                            }
                            onClick={function () {
                                if (offeredPlayerId !== "") {
                                    onSubmit(offeredPlayerId);
                                }
                            }}
                        >
                            {isSubmitting ? (
                                <>
                                    <IonSpinner name="crescent" />
                                    Enviando propuesta...
                                </>
                            ) : (
                                "Enviar propuesta"
                            )}
                        </IonButton>
                    </main>
                )}
            </IonContent>
        </IonModal>
    );
};

export default FantasyTradeOfferModal;
